import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { MedicalRecordService } from '../medical.service';
import { MedicalReportType, MedicalReportTypeLabels } from 'src/app/common/enums/medical-report-type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-medical-record-detail',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
  ],
  templateUrl: './medical-record-detail.component.html',
  styleUrl: './medical-record-detail.component.scss',
})
export class MedicalRecordDetailComponent implements OnInit {
  private static readonly allowedFileExtensions = ['.png', '.jpg', '.jpeg', '.pdf'];
  private static readonly allowedMimeTypes = ['image/png', 'image/jpeg', 'application/pdf'];
  private static readonly maxFileSizeInBytes = 10 * 1024 * 1024;

  uploadForm: FormGroup;
  medicalReportTypes: MedicalReportType[] = Object.values(MedicalReportType)
  .filter(value => typeof value === 'number') as MedicalReportType[];

  medicalReportTypeLabels = MedicalReportTypeLabels;

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) private _data: { patientId?: number, opdId?: number, ipdId?: number, defaultType?: MedicalReportType },
    private medicalRecordService:MedicalRecordService,
    private dialogRef: MatDialogRef<MedicalRecordDetailComponent>,
    private translationUtils: TranslationUtils,
    private translate: TranslateService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.uploadForm = this.fb.group({
      file: [null, Validators.required],
      type: [this._data.defaultType ?? MedicalReportType.PastDocument, Validators.required],
      patientId: [this._data.patientId],
      description: ['',Validators.maxLength(250)],
    });
  }

  onCloseDialog(response: any) {
    this.dialogRef.close(response);
  }

  selectedFile?: File;
  selectedFileName = '';

  get allowedFileTypesLabel(): string {
    return 'JPG, JPEG, PNG, PDF';
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const validationMessage = this.validateSelectedFile(file);
    if (validationMessage) {
      this.resetSelectedFile(input);
      this.uploadForm.get('file')?.setErrors({ invalidFile: true });
      this.uploadForm.get('file')?.markAsTouched();
      this.toastr.error(validationMessage);
      return;
    }

    if (file) {
      this.selectedFile = file;
      this.selectedFileName = file.name;

      this.uploadForm.patchValue({
        file: file,
      });
      this.uploadForm.get('file')?.setErrors(null);
      this.uploadForm.get('file')?.markAsDirty();
      this.uploadForm.get('file')?.updateValueAndValidity();
    }
  }

  onUploadFile() {
    this.uploadForm.markAllAsTouched();

    if (this.uploadForm.invalid) {
      if (this.uploadForm.get('file')?.hasError('required')) {
        this.toastr.error('Please choose a medical record file to upload.');
      }

      return;
    }

    if (this.uploadForm.value.type === MedicalReportType.IPD && !((this._data.ipdId ?? 0) > 0)) {
      this.toastr.error('Please select a valid IPD admission.');
      return;
    }
  
    const formValues = this.uploadForm.value;
    const formData = new FormData();
    formData.append('filePath', formValues.file);
    formData.append('patientId', formValues.patientId);
    formData.append('description', formValues.description);
    formData.append('type', formValues.type);
    if (this._data.opdId !== undefined) {
      formData.append('opdId', String(this._data.opdId));
    }
    if (this._data.ipdId !== undefined) {
      formData.append('ipdId', String(this._data.ipdId));
    }
    this.medicalRecordService.uploadMedicalRecord(formData).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.toastr.success(this.translate.instant('ENTITY.UPLOAD_MEDICAL_RECORD'));
          this.onCloseDialog(response);
          return;
        }

        const validationErrors = Array.isArray(response?.errors) ? response.errors : [];
        this.toastr.error(validationErrors.join('\n') || response?.message || 'File upload failed.');
      },
      error: (error) => {
        const validationErrors = Array.isArray(error?.error?.errors) ? error.error.errors : [];
        this.toastr.error(validationErrors.join('\n') || error?.error?.message || 'Error uploading file');
      }
    });
  }

  private validateSelectedFile(file: File): string | null {
    const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;

    if (!MedicalRecordDetailComponent.allowedFileExtensions.includes(extension)) {
      return `Only ${this.allowedFileTypesLabel} files are allowed.`;
    }

    if (file.type && !MedicalRecordDetailComponent.allowedMimeTypes.includes(file.type)) {
      return `Only ${this.allowedFileTypesLabel} files are allowed.`;
    }

    if (file.size > MedicalRecordDetailComponent.maxFileSizeInBytes) {
      return 'File size must be 10 MB or smaller.';
    }

    return null;
  }

  private resetSelectedFile(input: HTMLInputElement): void {
    this.selectedFileName = '';
    this.selectedFile = undefined;
    this.uploadForm.patchValue({ file: null });
    input.value = '';
  }
}
